import { slugForType } from '@/features/integrations/display-taxonomy'

/**
 * Where "Add integration" goes from every Network Collectors surface: the shared
 * catalog, on its **Network Logs** tab.
 *
 * One catalog, diverging at connect — browsing Network Logs from here is the same
 * act as opening the catalog and clicking the tab, so this is a link into the
 * existing surface rather than a second inline picker. Declared once because the
 * call sites (the list header, each expanded Collector's source panel, and the
 * detail page's header and empty state) present it differently and must not
 * drift to different destinations.
 *
 * **It carries the Collector when one is in scope.** A Log Source cannot exist
 * without a Collector, and the wizard's URL names one — so a surface that is
 * already about one Collector (its row's source panel, its detail page) passes
 * it through `?collector=`, and the card clicked next opens the wizard on it.
 * The list header has no Collector in scope and passes none; the card then picks
 * a default (`defaultCollectorIdFor`). Either way the operator can
 * still change it on the Register step's **Receive on collector** select.
 *
 * The slug comes from `slugForType`, the one owner of the type→slug mapping the
 * catalog reads back — a literal `'network-log'` here would be a second naming
 * scheme to keep in sync (ADR 0018 §4).
 */
export function networkLogCatalogLink(collectorId?: string) {
	return {
		to: '/integrations/new',
		search: { type: slugForType('Network Log'), collector: collectorId },
	} as const
}

/**
 * Where a **Network Logs** catalog card goes: the Add Log Source wizard, for
 * that Log Source Type on that Collector.
 *
 * Not `/integrations/new/$id`, which every Cloud card uses — that route runs the
 * server-driven connector setup form, and a Log Source has no connector to set
 * up. It has a listener and a Collector, which is a different screen (D12: one
 * catalog, diverging at connect).
 *
 * The Collector is a path segment rather than something handed across the
 * route boundary, so a refresh or a shared link lands on the same form for the
 * same Collector.
 *
 * Declared here beside the catalog link because the two are the same journey's
 * two halves, and a second spelling of this destination would be a second place
 * for the route to drift.
 */
export function networkLogWizardLink(
	definitionId: string,
	collectorId: string,
) {
	return {
		to: '/integrations/collectors/$collectorId/sources/new/$definitionId',
		params: { collectorId, definitionId },
	} as const
}
